/**
 * Checklist template helpers (tracker item 44): the Settings > Checklist
 * Templates form, its validation (mirrors the backend business layer so the
 * same messages show before a request is sent), and the Item picker options.
 */

import {
  CHECKLIST_STEP_TEXT_MAX,
  CHECKLIST_TEMPLATE_MAX_STEPS,
  CHECKLIST_TEMPLATE_NAME_MAX,
  ChecklistTemplate,
  ChecklistTemplateDeleteResult,
  ChecklistTemplateStepInput,
  CreateChecklistTemplateRequest,
} from '../api/types';

/** An editable step. `key` is local only: server step ids change on every save. */
export interface ChecklistStepDraft {
  key: string;
  text: string;
  required: boolean;
}

export interface ChecklistTemplateFormState {
  name: string;
  isActive: boolean;
  steps: ChecklistStepDraft[];
}

let draftCounter = 0;

export function newStepDraft(text = '', required = false): ChecklistStepDraft {
  draftCounter += 1;
  return { key: `step-${draftCounter}`, text, required };
}

/** A new template: active, with one blank step to fill in. */
export function emptyChecklistTemplateForm(): ChecklistTemplateFormState {
  return { name: '', isActive: true, steps: [newStepDraft()] };
}

export function checklistTemplateFormFromTemplate(template: ChecklistTemplate): ChecklistTemplateFormState {
  const steps = [...template.steps].sort((a, b) => a.sortOrder - b.sortOrder);
  return {
    name: template.name,
    isActive: template.isActive,
    steps: steps.map((step) => newStepDraft(step.text, step.required)),
  };
}

/**
 * The backend's create / replace body rules: name trimmed 1-255 characters;
 * 1-100 steps, each with text trimmed 1-500 characters. Errors name the
 * 1-based step. Returns null when valid.
 */
export function checklistTemplateBodyError(name: unknown, steps: unknown): string | null {
  if (typeof name !== 'string' || !name.trim()) return 'Template name is required';
  if (name.trim().length > CHECKLIST_TEMPLATE_NAME_MAX) {
    return `Template name cannot exceed ${CHECKLIST_TEMPLATE_NAME_MAX} characters`;
  }
  if (!Array.isArray(steps)) return 'steps must be an array';
  if (steps.length === 0) return 'A checklist template needs at least one step';
  if (steps.length > CHECKLIST_TEMPLATE_MAX_STEPS) {
    return `A checklist template can have at most ${CHECKLIST_TEMPLATE_MAX_STEPS} steps`;
  }
  for (let index = 0; index < steps.length; index += 1) {
    const step = steps[index] as Partial<ChecklistTemplateStepInput> | null;
    const position = index + 1;
    if (!step || typeof step !== 'object') return `Step ${position} must be an object`;
    if (typeof step.text !== 'string' || !step.text.trim()) return `Step ${position} text is required`;
    if (step.text.trim().length > CHECKLIST_STEP_TEXT_MAX) {
      return `Step ${position} text cannot exceed ${CHECKLIST_STEP_TEXT_MAX} characters`;
    }
    if (step.required !== undefined && typeof step.required !== 'boolean') {
      return `Step ${position} required must be true or false`;
    }
  }
  return null;
}

export function validateChecklistTemplateForm(form: ChecklistTemplateFormState): string | null {
  return checklistTemplateBodyError(form.name, form.steps);
}

/** Create / replace body: trimmed name and step text, steps in display order. */
export function buildChecklistTemplateRequest(form: ChecklistTemplateFormState): CreateChecklistTemplateRequest {
  return {
    name: form.name.trim(),
    isActive: form.isActive,
    steps: form.steps.map((step) => ({ text: step.text.trim(), required: step.required })),
  };
}

/** Moves the step at `index` by `delta` (-1 up, +1 down); out-of-range moves return the same array. */
export function moveStep<T>(steps: T[], index: number, delta: number): T[] {
  const target = index + delta;
  if (index < 0 || index >= steps.length || target < 0 || target >= steps.length) return steps;
  const next = [...steps];
  [next[index], next[target]] = [next[target], next[index]];
  return next;
}

/** Confirm prompt before DELETE, previewing the likely outcome from the template's usage counts. */
export function deleteConfirmMessage(template: ChecklistTemplate): string {
  if (!template.inUse) return `Delete checklist template "${template.name}"? This cannot be undone.`;
  const uses: string[] = [];
  if (template.itemCount > 0) uses.push(`${template.itemCount} item${template.itemCount === 1 ? '' : 's'}`);
  if (template.jobStepCount > 0) uses.push(`${template.jobStepCount} job checklist step${template.jobStepCount === 1 ? '' : 's'}`);
  return `"${template.name}" is used by ${uses.join(' and ')}, so it will be deactivated instead of deleted. Continue?`;
}

/** Outcome message after DELETE. Uses the server's result, not the counts seen earlier. */
export function deleteOutcomeMessage(result: ChecklistTemplateDeleteResult, name: string): string {
  return result.result === 'DELETED'
    ? `Checklist template "${name}" deleted.`
    : `Checklist template "${name}" is in use by items or jobs, so it was deactivated instead of deleted. ` +
        'Existing jobs keep their steps; it can no longer be linked to items.';
}

/**
 * Item picker options: active templates by name, plus the item's current
 * template when it has since been deactivated (so the select does not show
 * "None" while the form still holds its id).
 */
export function selectableChecklistTemplates(
  templates: ChecklistTemplate[],
  currentId?: string | null
): ChecklistTemplate[] {
  return templates
    .filter((template) => template.isActive || template.id === currentId)
    .sort((a, b) => a.name.localeCompare(b.name));
}
